import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { createOrUpdateUserDocument } from '../utilities/authUtils';
import { useFirebase } from './FirebaseContext';
import { identifyUser } from '../utilities/hotjarUtils';
import { logEvent, getAnalytics } from 'firebase/analytics';
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const { auth, db } = useFirebase();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  
  // Helper to log authentication errors
  const logAuthError = (error, action) => {
    try {
      let analytics;
      try {
        analytics = getAnalytics();
      } catch (analyticsError) {
        console.warn('Firebase Analytics not initialized:', analyticsError);
        analytics = null;
      }
      
      if (analytics) {
        logEvent(analytics, 'client_error', {
          error_type: 'authentication_error',
          error_message: error?.message?.substring(0, 100) || 'Unknown auth error',
          error_name: error?.name || 'AuthError',
          auth_action: action,
          user_id: currentUser?.uid || 'anonymous',
          timestamp: new Date().toISOString(),
          page_url: window.location.pathname
        });
      } else {
        // Fallback: log to console if analytics is unavailable
        console.error('Authentication error (not logged to Analytics):', {
          error_type: 'authentication_error',
          error_message: error?.message?.substring(0, 100) || 'Unknown auth error',
          error_name: error?.name || 'AuthError',
          auth_action: action,
          user_id: currentUser?.uid || 'anonymous',
          timestamp: new Date().toISOString(),
          page_url: window.location.pathname
        });
      }
    } catch (logError) {
      console.error('Failed to log authentication error:', logError);
    }
  };
  useEffect(() => {
    if (currentUser && currentUser?.uid) {
      identifyUser(currentUser.uid, {
        email: currentUser?.email || '',
      });
    }
  }, [currentUser]);

  useEffect(() => {
    if (!auth || !db) {
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // User is signed in, create or update the user document in Firestore
        try {
          // Check if the user document already exists
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            // User exists in Firestore, update last login time and set user profile
            const userData = userDoc.data();
            await createOrUpdateUserDocument(user, db, {
              lastLogin: new Date(),
            });

            setUserProfile(userData);
          } else {
            // User doesn't exist in Firestore, create a new document
            await createOrUpdateUserDocument(user, db, {
              createdAt: new Date(),
              lastLogin: new Date(),
            });

            // Fetch the newly created user document
            const newUserDoc = await getDoc(userRef);
            setUserProfile({ ...newUserDoc.data(), id: user.uid, uid: user.uid });
          }
        } catch (error) {
          console.error('Error saving user to Firestore:', error);
          logAuthError(error, 'user_document_creation');
        }
      } else {
        // User is signed out
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, db]);

  // Function to update user profile in Firestore
  const updateUserProfile = async (profileData) => {
    if (!currentUser) return false;

    try {
      // Update the user document in Firestore
      await createOrUpdateUserDocument(currentUser, db, {
        ...profileData,
        updatedAt: new Date(),
      });

      // Update local state - preserve all existing fields
      setUserProfile((prev) => ({
        ...prev,
        ...profileData,
        updatedAt: new Date(),
      }));

      return true;
    } catch (error) {
      console.error('Error updating user profile:', error);
      logAuthError(error, 'profile_update');
      return false;
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    updateUserProfile,
    setCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
